import React from 'react';
import '../../styles/ReinsUpdater.css';
import {utils, write, CFB} from 'xlsx';
import axios from 'axios';
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import LoadingButton from "@mui/lab/LoadingButton";
import DownloadIcon from "@mui/icons-material/Download";
import {GlobalConstants} from "../../globals/GlobalConstants";
import {refreshToken} from "../../globals/GlobalFunctions";
import {FILE_KINDS, DATE_COLUMNS} from "./ReinsContractFiles";

//only one contract at a time for now
//The backend already takes a list so setting this to true turns on picking more than one
const ALLOW_MULTIPLE = false;

//one contract in the pick list
type ContractOption = {
    reinsContract_Key: number;
    contract_ID: string | null;
    effective_Date: string | null;
    term_Date: string | null;
    division_ID: string | null;
    group_ID: string | null;
    description: string | null;
    contract_Status: string | null;
};

//what shows in the pick list, example: ABC123 01/01/2025 - 12/31/2025 division 00000001
const contractLabel = (contract: ContractOption) =>
    `${contract.contract_ID} ${contract.effective_Date} - ${contract.term_Date}` +
    (contract.division_ID ? ` division ${contract.division_ID}` : '') +
    (contract.description ? ` (${contract.description})` : '');

//excel stores a date as the number of days since 12/30/1899
//this conversion allows us to translate vba to excel
const excelDate = (text: string) => {
    const [month, day, year] = text.split('/').map(Number);
    return Date.UTC(year, month - 1, day) / 86400000 + 25569;
};

//one files tab, header row first then a row per value
//rows are {column header: value}, a null value is left as a blank cell
const buildSheet = (columns: string[], rows: any[]) => {
    const sheet: any = {};

    columns.forEach((column, c) => {
        sheet[utils.encode_cell({r: 0, c: c})] = {t: 's', v: column};
    });

    rows.forEach((row, r) => {
        columns.forEach((column, c) => {
            const value = row[column];
            if (value === null || value === undefined) return;

            let cell;
            if (DATE_COLUMNS.includes(column)) {
                cell = {t: 'n', v: excelDate(value), z: 'mm/dd/yyyy'};
            }
            else if (typeof value === 'number') {
                cell = {t: 'n', v: value};
            }
            else {
                cell = {t: 's', v: String(value)};
            }
            sheet[utils.encode_cell({r: r + 1, c: c})] = cell;
        });
    });

    sheet['!ref'] = utils.encode_range({s: {r: 0, c: 0}, e: {r: rows.length, c: columns.length - 1}});
    sheet['!cols'] = columns.map(column => ({wch: Math.max(12, column.length + 4)}));
    return sheet;
};

//scrub for odd characters so the string can be in a file name
const safeFileName = (text: string) => text.replace(/[\\/:*?"<>|]/g, '_');

function ReinsContractExporter() {
    const [currentDB, setCurrentDB] = React.useState("CoastalAdmin");
    const [contracts, setContracts] = React.useState<ContractOption[]>([]);
    const [loadingList, setLoadingList] = React.useState(false);
    const [selected, setSelected] = React.useState<ContractOption[]>([]);
    const [showClosed, setShowClosed] = React.useState(false);
    const [exporting, setExporting] = React.useState(false);
    const [message, setMessage] = React.useState('');
    const [errors, setErrors] = React.useState<string[]>([]);

    const onDBChange = React.useCallback((e: any, newDB: any) => setCurrentDB(newDB.id), []);

    const shownContracts = contracts.filter(contract => showClosed || contract.contract_Status === 'OPEN');

    //hiding closed contracts also takes them out of what is picked
    const onShowClosedChange = (checked: boolean) => {
        setShowClosed(checked);
        if (!checked) {
            setSelected(selected.filter(contract => contract.contract_Status === 'OPEN'));
        }
    };

    //loads the pick list whenever the database changes
    React.useEffect(() => {
        const loadContracts = async () => {
            setLoadingList(true);
            setContracts([]);
            setSelected([]);
            setMessage('');
            setErrors([]);
            refreshToken();

            try {
                const response = await axios.get(
                    localStorage.getItem('baseURL') + 'reinscontract/exportList/' + currentDB,
                    {headers: {Authorization: 'Bearer ' + localStorage.getItem('jwt')}}
                );
                setContracts(response.data);
            } catch (err: any) {
                setErrors(['The contract list could not be loaded. ' + (err?.message ?? '')]);
            }
            setLoadingList(false);
        };

        loadContracts();
    }, [currentDB]);

    //the folder inside the zip
    const folderName = () => {
        const today = new Date();
        const datePart = `${String(today.getMonth() + 1).padStart(2, '0')}_${String(today.getDate()).padStart(2, '0')}_${today.getFullYear()}`;

        if (selected.length === 1 && selected[0].contract_ID) {
            return `Reinsurance Contracts - ${safeFileName(selected[0].contract_ID)} ${datePart}`;
        }
        return `Reinsurance Contracts ${datePart}`;
    };

    //downloads one zip with the folder inside it and the 4 files in the folder
    const saveFiles = (data: any, name: string) => {
        const zip = CFB.utils.cfb_new();

        for (const kind of FILE_KINDS) {
            const workbook = utils.book_new();
            utils.book_append_sheet(workbook, buildSheet(kind.columns, data[kind.name] ?? []), kind.name);

            //the zip needs the bytes as a Uint8Array, an ArrayBuffer comes out empty
            const bytes = new Uint8Array(write(workbook, {bookType: 'xlsx', type: 'array'}));
            CFB.utils.cfb_add(zip, `/${name}/Reinsurance ${kind.name}.xlsx`, bytes);
        }

        const blob = new Blob([CFB.write(zip, {fileType: 'zip', type: 'array'})], {type: 'application/zip'});
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = name + '.zip';
        link.click();

        window.URL.revokeObjectURL(url);
    };

    const exportContracts = async () => {
        if (selected.length === 0) return;

        setMessage('');
        setErrors([]);

        refreshToken();
        setExporting(true);

        try {
            const response = await axios.post(
                localStorage.getItem('baseURL') + 'reinscontract/export',
                {
                    vbasoftware_database: currentDB,
                    reinsContract_Keys: selected.map(contract => contract.reinsContract_Key)
                },
                {headers: {Authorization: 'Bearer ' + localStorage.getItem('jwt')}}
            );

            const name = folderName();
            saveFiles(response.data, name);
            setMessage(`${response.data.message} ${name}.zip is in your downloads.`);
        } catch (err: any) {
            if (err?.response?.data?.errors) {
                setErrors(err.response.data.errors);
            }
            else {
                setErrors(['The export failed. ' + (err?.message ?? '')]);
            }
        }
        setExporting(false);
    };

    return (
        <div className="pageWrapReins">
            <div className="reinsReportCard">
                <span className="reinsLabel">
                    {ALLOW_MULTIPLE ? 'Contracts To Export:' : 'Contract To Export:'}
                </span>

                {ALLOW_MULTIPLE ? (
                    <Autocomplete
                        multiple
                        size={"small"}
                        disablePortal
                        options={shownContracts}
                        value={selected}
                        getOptionLabel={contractLabel}
                        isOptionEqualToValue={(option, value) => option.reinsContract_Key === value.reinsContract_Key}
                        loading={loadingList}
                        disabled={exporting}
                        sx={{width: '100%', backgroundColor: '#FFFFFF', borderRadius: '5px'}}
                        renderInput={(params) => <TextField {...params} placeholder="Type to search"/>}
                        onChange={(e, newValue) => setSelected(newValue)}
                    />
                ) : (
                    <Autocomplete
                        size={"small"}
                        disablePortal
                        options={shownContracts}
                        value={selected[0] ?? null}
                        getOptionLabel={contractLabel}
                        isOptionEqualToValue={(option, value) => option.reinsContract_Key === value.reinsContract_Key}
                        loading={loadingList}
                        disabled={exporting}
                        sx={{width: '100%', backgroundColor: '#FFFFFF', borderRadius: '5px'}}
                        renderInput={(params) => <TextField {...params} placeholder="Type to search"/>}
                        onChange={(e, newValue) => setSelected(newValue ? [newValue] : [])}
                    />
                )}

                <FormControlLabel
                    control={<Checkbox checked={showClosed} onChange={e => onShowClosedChange(e.target.checked)} disabled={exporting}/>}
                    label="Show Closed Contracts"
                    sx={{color: 'white', marginTop: '16px'}}
                />

                <div className="buttonWrapper">
                    <LoadingButton
                        className="buttonz"
                        variant="contained"
                        onClick={exportContracts}
                        loading={exporting}
                        loadingPosition="start"
                        startIcon={<DownloadIcon/>}
                        disabled={selected.length === 0}
                        sx={{'&.Mui-disabled': {color: 'rgba(255, 255, 255, 0.6)'}}}
                    >
                        Export
                    </LoadingButton>
                </div>

                {message && (
                    <Alert severity="success" sx={{marginTop: '20px'}} onClose={() => setMessage('')}>
                        {message}
                    </Alert>
                )}

                {errors.length > 0 && (
                    <Alert severity="error" sx={{marginTop: '20px'}} onClose={() => setErrors([])}>
                        {errors.map(problem => <div key={problem}>{problem}</div>)}
                    </Alert>
                )}
            </div>

            <Stack spacing={5} direction="row" className="buttonBarReins">
                <Autocomplete
                    className="dbContractSelectReins"
                    size={"small"}
                    disablePortal
                    disableClearable
                    options={GlobalConstants.VBA_DB}
                    value={GlobalConstants.VBA_DB.find(option => option.id === currentDB) || null}
                    sx={{ width: 150 }}
                    renderInput={(params) => <TextField {...params}  />}
                    onChange={onDBChange}
                    disabled={exporting}
                />
            </Stack>
        </div>
    );
}

export default React.memo(ReinsContractExporter);
